import { StateComponent } from '@highimpact/dashboard-ui';
import { forwardRef, LegacyRef, useMemo } from 'react';
import Plot from '../../../../internal/plot/Plot';
import { convertIsoToPlotlyFormat } from '../../../../internal/utils/date';
import {
  buildOmbrothermicAxisGroups,
  resolveElementMeasureColumnNames,
} from '../../../../runtime/buildElementPlotMetadata';
import { buildPlotlyLayoutFromElement } from '../../../../runtime/buildPlotlyLayoutFromElement';
import { getDefaultConfig, PlotProps } from '../../commons/plots/helpers';
import { getDefaultLayout } from '../../commons/plots/plotly-operations';
import { usePlotPngInstanceRegistration } from '../shared/usePlotPngInstanceRegistration';
import { useStaticElementMeasureRows } from '../shared/useStaticElementMeasureRows';

function OmbrothermicPlot(
  { dataset, elementData, dashboardElementId, widget_id }: PlotProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const { rows, loading, error, usesElementMeasureRequest, labelMap } =
    useStaticElementMeasureRows({
      element: elementData,
      plotType: 'ombrothermic',
    });

  const plotName = elementData.name ?? 'Plot';
  usePlotPngInstanceRegistration({
    dashboardElementId,
    widgetId: widget_id,
    plotName,
    enabled: !(usesElementMeasureRequest && (loading || error)),
  });
  const columnNames = useMemo(
    () => resolveElementMeasureColumnNames(elementData, labelMap),
    [elementData, labelMap]
  );
  const yGroups = useMemo(
    () => buildOmbrothermicAxisGroups(elementData),
    [elementData]
  );
  const layout = useMemo(
    () => getDefaultLayout(buildPlotlyLayoutFromElement(elementData)),
    [elementData]
  );

  const measureRows = usesElementMeasureRequest
    ? rows ?? []
    : dataset?.data ?? [];

  if (usesElementMeasureRequest && loading) {
    return (
      <div ref={ref} className="widget-content">
        <StateComponent
          name="Veuillez patienter !"
          message="Chargement en cours"
          spinner
        />
      </div>
    );
  }

  if (usesElementMeasureRequest && error) {
    return (
      <div ref={ref} className="widget-content">
        <div>Impossible de charger les données ombrothermiques.</div>
      </div>
    );
  }

  if (columnNames.length < 2 || yGroups.length < 2) {
    return (
      <div ref={ref} className="widget-content">
        <div>État incohérent de l&apos;élément ombrothermique.</div>
      </div>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const precipitationName = columnNames[0]!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const temperatureName = columnNames[1]!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const precipitationAxis = yGroups[0]!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const temperatureAxis = yGroups[1]!;

  const trace1: Partial<Plotly.PlotData> = {
    name: precipitationName,
    x: measureRows.map((data) =>
      convertIsoToPlotlyFormat(String(data.Temps ?? ''))
    ),
    y: measureRows.map((data) => data[precipitationName] as number),
    xaxis: 'x',
    yaxis: 'y',
    mode: 'lines',
    hovertemplate: `%{y:,.2f} ${precipitationAxis.unit}`,
    line: { width: 4 },
    marker: { size: 12 },
    type: 'bar',
  };

  const trace2: Partial<Plotly.PlotData> = {
    name: temperatureName,
    x: measureRows.map((data) => {
      const raw = data.Temps;
      if (typeof raw === 'number') {
        return raw;
      }
      const parsed = new Date(String(raw));
      return Number.isNaN(parsed.getTime()) ? 0 : parsed.getMonth() + 1;
    }),
    y: measureRows.map((data) => data[temperatureName] as number),
    xaxis: 'x2',
    yaxis: 'y2',
    mode: 'lines',
    hovertemplate: `%{y:,.2f} ${temperatureAxis.unit}`,
    line: { width: 1 },
    marker: { size: 12 },
    type: 'scatter',
  };

  return (
    <div ref={ref} className="widget-content">
      <Plot
        className="plot"
        layout={layout}
        data={[trace1, trace2]}
        config={getDefaultConfig(plotName)}
      />
    </div>
  );
}

export default forwardRef(OmbrothermicPlot);
